import { useEffect, useMemo } from 'react';
import {
  GroupsService,
  GroupSettingsResponse,
  TrackingEntry,
} from 'src/services/groups';
import { useGeneralStore } from 'src/store';

const COLOR_VAR_MAP: Array<
  [keyof GroupSettingsResponse['settings']['colors'], string]
> = [
  ['denary', '--color-denary'],
  ['nonary', '--color-view-btn'],
  ['senary', '--color-border'],
  ['primary', '--color-primary'],
  ['quinary', '--color-text'],
  ['octonary', '--color-background'],
  ['tertiary', '--destructive'],
  ['secondary', '--color-profile-wellcome'],
  ['septenary', '--color-border-select'],
  ['quaternary', '--color-error'],
];

const GOOGLE_FONTS_MAP: Record<string, string> = {
  Inter: 'Inter',
  Manrope: 'Manrope',
  Raleway: 'Raleway',
};

const FONT_MAP: Record<string, string> = {
  Inter: "'Inter', sans-serif",
  Manrope: "'Manrope', sans-serif",
  Raleway: "'Raleway', sans-serif",
  HelveticaNow:
    "'HelveticaNow', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Helvetica, Arial, sans-serif",
};
const TRACKING_ID_RE = /^[A-Za-z0-9-]+$/;
type GoogleTrackingIds = {
  googleIds: string[];
  googleTagManagerIds: string[];
};

type GoogleTrackingWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
};

type GooglePageUpdateParams = {
  pagePath?: string;
  pageTitle?: string;
  pageLocation?: string;
  pageReferrer?: string;
  pageType?: 'route' | 'form_section';
  screenName?: string;
  formSection?: string;
};

type GooglePageUpdatePayload = {
  page_title: string;
  page_location: string;
  page_path: string;
  page_referrer?: string;
  page_type?: string;
  screen_name?: string;
  form_section?: string;
};

let lastTrackedPageLocation: string | null = null;

function getTrackerIdsFromEnv(...envNames: string[]): string[] {
  const env = import.meta.env as unknown as Record<string, string | undefined>;

  return envNames
    .flatMap((envName) => (env[envName] || '').split(','))
    .map((id) => id.trim())
    .filter((id) => TRACKING_ID_RE.test(id));
}

function getUniqueIds(ids: string[]): string[] {
  const seen = new Set<string>();

  return ids.filter((id) => {
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

function normalizeTrackingProvider(
  provider?: TrackingEntry['provider']
): string {
  return (provider || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

function isGoogleProvider(provider?: TrackingEntry['provider']): boolean {
  const normalizedProvider = normalizeTrackingProvider(provider);

  return (
    normalizedProvider === 'google' ||
    normalizedProvider === 'gtag' ||
    normalizedProvider === 'googlepixel'
  );
}

function isGoogleTagManagerProvider(
  provider?: TrackingEntry['provider']
): boolean {
  const normalizedProvider = normalizeTrackingProvider(provider);

  return (
    normalizedProvider === 'googletagmanager' || normalizedProvider === 'gtm'
  );
}

function getConfiguredGoogleTrackingIds(
  tracking?: TrackingEntry[] | null
): GoogleTrackingIds {
  const googleIds = getTrackerIdsFromEnv('VITE_TRACKER_GOOGLE');
  const googleTagManagerIds = getTrackerIdsFromEnv(
    'VITE_TRACKER_GOOGLE_TAG_MANAGER',
    'VITE_TRACKER_GOOGLE_TAGMANAGER',
    'VITE_TRACKER_GTM'
  );

  if (Array.isArray(tracking)) {
    tracking.forEach((entry) => {
      const id = entry?.value?.trim();
      if (!id || !TRACKING_ID_RE.test(id)) return;

      if (isGoogleProvider(entry?.provider)) {
        googleIds.push(id);
      } else if (isGoogleTagManagerProvider(entry?.provider)) {
        googleTagManagerIds.push(id);
      }
    });
  }

  return {
    googleIds: getUniqueIds(googleIds),
    googleTagManagerIds: getUniqueIds(googleTagManagerIds),
  };
}

export function getGoogleTrackingKey(
  tracking?: TrackingEntry[] | null
): string {
  const { googleIds, googleTagManagerIds } =
    getConfiguredGoogleTrackingIds(tracking);

  return [googleIds.join(','), googleTagManagerIds.join(',')].join('|');
}

export function hasGoogleTracking(tracking?: TrackingEntry[] | null): boolean {
  const { googleIds, googleTagManagerIds } =
    getConfiguredGoogleTrackingIds(tracking);

  return googleIds.length > 0 || googleTagManagerIds.length > 0;
}

function isDevPixelConsoleEnabled(): boolean {
  if (typeof window === 'undefined') return false;

  const env = import.meta.env as unknown as Record<string, string | undefined>;
  const envName = env.VITE_APP_ENV;
  const stage = env.VITE_APP_STAGE || '';
  const hostname = window.location.hostname;

  return (
    import.meta.env.DEV ||
    envName === 'development' ||
    hostname === 'localhost' ||
    hostname.includes('.dev.') ||
    stage.includes('.dev.')
  );
}

function normalizePagePath(path: string): string {
  return path.startsWith('/') ? path : `/${path}`;
}

function buildGooglePageUpdate(
  params: GooglePageUpdateParams = {}
): GooglePageUpdatePayload {
  const pagePath = normalizePagePath(
    params.pagePath ||
      `${window.location.pathname}${window.location.search}${window.location.hash}`
  );
  const pageLocation =
    params.pageLocation || `${window.location.origin}${pagePath}`;
  const pageReferrer =
    params.pageReferrer || lastTrackedPageLocation || document.referrer;

  return {
    page_title: params.pageTitle || document.title,
    page_location: pageLocation,
    page_path: pagePath,
    page_referrer: pageReferrer || undefined,
    ...(params.pageType && { page_type: params.pageType }),
    ...(params.screenName && { screen_name: params.screenName }),
    ...(params.formSection && { form_section: params.formSection }),
  };
}

function logDevPixelUpdate(
  provider: 'google' | 'googleTagManager',
  payload: Record<string, unknown>
): void {
  if (!isDevPixelConsoleEnabled()) return;
  console.info('[pixel:update]', {
    provider,
    ...payload,
  });
}

export function pushGooglePageUpdate(
  tracking?: TrackingEntry[] | null,
  params: GooglePageUpdateParams = {}
): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const { googleIds, googleTagManagerIds } =
    getConfiguredGoogleTrackingIds(tracking);

  if (googleIds.length === 0 && googleTagManagerIds.length === 0) return;

  const win = window as GoogleTrackingWindow;
  const pageUpdate = buildGooglePageUpdate(params);

  googleIds.forEach((id) => {
    const payload = {
      send_to: id,
      ...pageUpdate,
    };

    if (typeof win.gtag === 'function') {
      win.gtag?.('event', 'page_view', {
        ...payload,
      });
    }

    logDevPixelUpdate('google', {
      action: "gtag('event', 'page_view')",
      id,
      sent: typeof win.gtag === 'function',
      payload,
    });
  });

  if (googleTagManagerIds.length > 0) {
    win.dataLayer = win.dataLayer || [];
    const payload = {
      event: 'virtual_page_view',
      gtm_container_ids: googleTagManagerIds,
      ...pageUpdate,
    };

    win.dataLayer.push(payload);

    logDevPixelUpdate('googleTagManager', {
      action: 'dataLayer.push',
      sent: true,
      payload,
    });
  }

  lastTrackedPageLocation = pageUpdate.page_location;
}

export function pushGoogleFormPageUpdate(
  tracking: TrackingEntry[] | null | undefined,
  params: {
    screenPath: string;
    screenName: string;
    pageTitle?: string;
  }
): void {
  pushGooglePageUpdate(tracking, {
    pagePath: `/form/${params.screenPath.replace(/^\/+/, '')}`,
    pageTitle: params.pageTitle || `Form: ${params.screenName}`,
    pageType: 'form_section',
    screenName: params.screenName,
    formSection: params.screenPath,
  });
}

function loadGoogleFont(fontName: string): void {
  if (typeof document === 'undefined') return;

  const normalizedName = fontName.trim().replace(/['"]/g, '');
  const googleFontName = GOOGLE_FONTS_MAP[normalizedName];

  if (!googleFontName) return;

  const existingLink = document.querySelector(
    `link[href*="${googleFontName}"]`
  );
  if (existingLink) return;

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${googleFontName}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}
const activeSkin = import.meta.env.VITE_APP_SKIN || 'lolly';
function applyCssVariables(
  colors: GroupSettingsResponse['settings']['colors'],
  backgroundImage?: string,
  fonts?: {
    primary?: string;
    secondary?: string | null;
    tertiary?: string | null;
  }
) {
  const root = document.body;
  COLOR_VAR_MAP.forEach(([apiKey, cssVar]) => {
    const value = colors[apiKey];
    if (value) {
      root.style.setProperty(cssVar, value);
      if (cssVar === '--color-background') {
        root.style.setProperty('--background', value);
      }
      if (cssVar === '--color-primary') {
        root.style.setProperty('--primary', value);
        if (!import.meta.env.VITE_APP_SKIN) {
          root.style.setProperty('--color-user-message-primary', value);
        } else {
          // lolly
          root.style.setProperty('--color-user-message-primary', '#e7e4de');
        }
      }
      if (cssVar === '--destructive') {
        root.style.setProperty('--color-destructive', value);
      }
    }
  });

  if (backgroundImage) {
    root.style.setProperty('--background-image', `url(${backgroundImage})`);
    root.style.setProperty(
      '--color-background-image',
      `url(${backgroundImage})`
    );
  } else {
    root.style.setProperty('--background-image', 'none');
    root.style.setProperty('--color-background-image', 'none');
  }

  if (fonts) {
    if (fonts.primary && fonts.primary.trim()) {
      const normalizedPrimary = fonts.primary.trim();
      if (GOOGLE_FONTS_MAP[normalizedPrimary]) {
        loadGoogleFont(normalizedPrimary);
      }
      root.style.setProperty(
        '--font-primary',
        FONT_MAP[normalizedPrimary] ?? normalizedPrimary
      );
    }
    if (fonts.secondary && fonts.secondary.trim()) {
      const normalizedSecondary = fonts.secondary.trim();
      if (GOOGLE_FONTS_MAP[normalizedSecondary]) {
        loadGoogleFont(normalizedSecondary);
      }
      root.style.setProperty(
        '--font-secondary',
        FONT_MAP[normalizedSecondary] ?? normalizedSecondary
      );
    }
    if (fonts.tertiary && fonts.tertiary.trim()) {
      const normalizedTertiary = fonts.tertiary.trim();
      if (GOOGLE_FONTS_MAP[normalizedTertiary]) {
        loadGoogleFont(normalizedTertiary);
      }
      root.style.setProperty(
        '--font-tertiary',
        FONT_MAP[normalizedTertiary] ?? normalizedTertiary
      );
    }
  }
}

function injectScript(id: string, opts: { src?: string; inner?: string }) {
  if (typeof document === 'undefined' || document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  if (opts.src) {
    script.src = opts.src;
    script.async = true;
  }
  if (opts.inner) script.innerHTML = opts.inner;
  document.head.appendChild(script);
}

function injectGoogleTagManagerNoScript(elementId: string, trackingId: string) {
  if (typeof document === 'undefined' || document.getElementById(elementId)) {
    return;
  }

  const inject = () => {
    if (!document.body || document.getElementById(elementId)) return;
    const noscript = document.createElement('noscript');
    noscript.id = elementId;

    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${trackingId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';

    noscript.appendChild(iframe);
    document.body.prepend(noscript);
  };

  if (document.body) {
    inject();
  } else {
    window.addEventListener('DOMContentLoaded', inject, { once: true });
  }
}

function applyTracking(tracking?: TrackingEntry[] | null) {
  if (typeof document === 'undefined' || !Array.isArray(tracking)) return;

  tracking.forEach((entry) => {
    const id = entry?.value?.trim();
    if (!id || !TRACKING_ID_RE.test(id)) return;

    if (isGoogleProvider(entry?.provider)) {
      injectScript(`tracking-google-loader-${id}`, {
        src: `https://www.googletagmanager.com/gtag/js?id=${id}`,
      });
      injectScript(`tracking-google-init-${id}`, {
        inner: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${id}');`,
      });
    } else if (isGoogleTagManagerProvider(entry?.provider)) {
      injectScript(`tracking-google-tag-manager-${id}`, {
        inner: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${id}');`,
      });
      injectGoogleTagManagerNoScript(
        `tracking-google-tag-manager-noscript-${id}`,
        id
      );
    } else if (
      normalizeTrackingProvider(entry?.provider) === 'facebook' ||
      normalizeTrackingProvider(entry?.provider) === 'meta'
    ) {
      injectScript(`tracking-facebook-${id}`, {
        inner: `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${id}');fbq('track','PageView');`,
      });
    }
  });
}

function updateMetaTags(group: GroupSettingsResponse) {
  const titleElement = document.querySelector('title[data-title="1"]');
  if (titleElement && group.name) {
    titleElement.textContent = group.name;
  }

  const descriptionElements = document.querySelectorAll(
    '[data-meta-description="1"]'
  );
  descriptionElements.forEach((el) => {
    if (group.description) {
      el.setAttribute('content', group.description || '');
    }
  });

  const titleElements = document.querySelectorAll('[data-meta-title="1"]');
  titleElements.forEach((el) => {
    if (group.name) {
      el.setAttribute('content', group.name);
    }
  });

  // const hostElements = document.querySelectorAll('[data-meta-host="1"]');
  // const hostUrl = group.host ? `https://${group.host}` : window.location.href;
  // hostElements.forEach((el) => {
  //   el.setAttribute('content', hostUrl);
  // });

  const imageElements = document.querySelectorAll('[data-meta-image="1"]');
  const welcomeImage = group.settings?.images?.welcome || '';
  imageElements.forEach((el) => {
    if (welcomeImage) {
      el.setAttribute('content', welcomeImage);
    }
  });

  const faviconMetaElements = document.querySelectorAll(
    '[data-meta-favicon="1"]'
  );
  const favicon = group.settings?.images?.favIcon || '';
  faviconMetaElements.forEach((el) => {
    if (favicon) {
      el.setAttribute('content', favicon);
    }
  });

  const faviconLink = document.querySelector('link[data-favicon="1"]');
  if (faviconLink && group.settings?.images?.favIcon) {
    faviconLink.setAttribute('href', group.settings.images.favIcon);
  }
}

export function useBranding() {
  const setSiteConfig = useGeneralStore((s) => s.setSiteConfig);
  const setIsLoadingConfig = useGeneralStore((s) => s.setIsLoadingConfig);
  const setConfigLoadFailed = useGeneralStore((s) => s.setConfigLoadFailed);

  const groupHash = useMemo(() => {
    if (typeof document === 'undefined') return null;
    return (
      import.meta.env.VITE_APP_GROUP_HASH ||
      document
        .querySelector('meta[name="x-group-hash"]')
        ?.getAttribute('content')
    );
  }, []);

  useEffect(() => {
    if (!groupHash) {
      // No group hash configured (local dev / storybook): no host to resolve, so
      // this is NOT a resolution failure — leave the fail-closed flag off.
      setConfigLoadFailed(false);
      setIsLoadingConfig(false);
      return;
    }
    let cancelled = false;
    // Fresh attempt: clear any prior failure latch before we know the outcome.
    setConfigLoadFailed(false);
    (async () => {
      try {
        const group = await GroupsService.getByHash(groupHash);
        if (cancelled) return;

        // Update all meta tags by their data attributes
        updateMetaTags(group);

        applyTracking(group.settings?.tracking);

        const planIds = group.plans?.map((plan) => plan.planId) || [];

        // store non-color settings in siteConfig for UI usage
        setSiteConfig({
          plans: planIds,
          phqEnabled: group.phqEnabled ?? true,
          logo: group.settings?.images?.logo || '',
          groupType: group.type,
          webClientType: group.webClientType,
          theme: 'theme-default',
          welcomeTitle: group.settings.texts?.welcomeTitle || '',
          welcomeDescription: group.settings.texts?.welcomeText || '',
          registrationTitle: group.settings.texts?.registrationTitle || '',
          registrationText: group.settings.texts?.registrationText || '',
          images: group.settings.images || null,
          emails: group.settings.emails || null,
          phones: group.settings.phones || null,
          skin: activeSkin || null,
          landing: group.settings.landing || null,
          tracking: group.settings.tracking || null,
        });
        // apply CSS variables at runtime
        applyCssVariables(
          group.settings.colors,
          group.settings.images?.background,
          group.settings.fonts
        );
      } catch (e) {
        // Branding fetch failed. Don't block the app, but latch the failure so
        // the W2 host gate can fail CLOSED on an unresolved host (finding #2)
        // instead of leaving a possibly-W2 host fully open. A returning browser
        // with a persisted `siteConfig.webClientType` is unaffected — the gate
        // reads that first.
        if (!cancelled) setConfigLoadFailed(true);
        // console.warn('Branding load failed', e);
      } finally {
        if (!cancelled) {
          setTimeout(() => setIsLoadingConfig(false), 200);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [groupHash, setSiteConfig, setIsLoadingConfig, setConfigLoadFailed]);
}
